import { FC, useEffect, useRef, useState } from "react";

import { ProviderRegistration } from "@asd/customer-portal-api";
import { Box, CircularProgress, Typography } from "@mui/material";

import { useKeycloak } from "~/components/authorization";
import { ProviderRegistrationService } from "~/services";
import { mapToUiGeneralAvailability } from "~/utils/providerRegistrationHelpers";

interface ProviderHomeProps {
  providerRegistration: ProviderRegistration;
}

export const withProviderData = (WrappedComponent: FC<ProviderHomeProps>) => {
  const WrappedProviderHome = (props: any) => {
    const keycloak = useKeycloak();

    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [providerRegistrationData, setProviderRegistrationData] = useState<null | ProviderRegistration>(null);

    const abortController = useRef<AbortController>(new AbortController());

    useEffect(() => {
      const controller = abortController.current;

      return () => {
        controller?.abort();
      };
    }, []);

    useEffect(() => {
      const providerId = keycloak.userState.providerRegistrationId;
      if (!providerId) {
        setHasError(true);
        setIsLoading(false);
        return;
      }

      const fetchData = async () => {
        try {
          const response = await ProviderRegistrationService.getProviderRegistration(providerId);

          if (response?.status === 200 && response?.data) {
            const normalizedRegistration = {
              ...response.data,
              generalAvailability: mapToUiGeneralAvailability(response.data.generalAvailability),
            };
            setProviderRegistrationData(normalizedRegistration);
          } else {
            throw new Error("Failed to fetch");
          }
        } catch (err: any) {
          if (err.name === "CanceledError" || err.name === "AbortError") {
            return; // Ignore cleanup-related cancels
          }
          setHasError(true);
        } finally {
          setIsLoading(false);
        }
      };

      fetchData();
    }, [keycloak.userState.providerRegistrationId]);

    if (isLoading) {
      return (
        <Box>
          <CircularProgress size={150} sx={{ display: "block", margin: "auto" }} />
        </Box>
      );
    }

    if (hasError || !providerRegistrationData) {
      return (
        <Typography align="center" sx={{ mt: 10 }} variant="h6">
          Unable to retrieve Provider information
        </Typography>
      );
    }

    return <WrappedComponent providerRegistration={providerRegistrationData} {...props} />;
  };

  return WrappedProviderHome;
};
